import { useDecision } from '@optimizely/react-sdk';
import DynamicForm, { FormValuesOutput } from '@policygenius/mortar-dynamic-forms';
import { useCreateDataPointValues, useGetDataPointValues } from 'api/hooks/useDataPointValues';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { completedNavigatorStep } from 'analytics/navigator';
import { StepName } from 'analytics/navigator/constants';
import DynamicFormWrapper from 'components/DynamicFormWrapper';
import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import {
  CONTINUE_ONLINE_FINANCES_PATH,
  CONTINUE_ONLINE_MILITARY_SERVICE_PATH,
} from 'routes/constants';
import useSelector from 'store/useSelector';
import { Flags } from 'util/optimizely/types';

import schema from './schema';
import { FormValues } from './types';

export const ADVERSE_DRIVING_URNS: Array<keyof FormValues> = [
  'urn:pg:data-point:insured-risk-activity-driving-history-dwi-conviction-history',
  'urn:pg:data-point:insured-risk-activity-driving-history-dwi-conviction-date',
  'urn:pg:data-point:insured-risk-activity-adverse-driving-reckless-driving-history',
  'urn:pg:data-point:insured-risk-activity-driving-history-reckless-driving-conviction-date',
  'urn:pg:data-point:insured-risk-activity-adverse-driving-license-revoked-or-suspended',
  'urn:pg:data-point:insured-risk-activity-adverse-driving-drivers-license-suspended-date',
  'urn:pg:data-point:insured-risk-activity-adverse-driving-moving-violations-zero-to-six-months',
  'urn:pg:data-point:insured-risk-activity-adverse-driving-moving-violations-six-to-twelve-months',
  'urn:pg:data-point:insured-risk-activity-adverse-driving-moving-violations-twelve-to-twentyfour-months',
  'urn:pg:data-point:insured-risk-activity-adverse-driving-moving-violations-twentyfour-to-thirtysix-months',
  'urn:pg:data-point:insured-risk-activity-adverse-driving-moving-violations-thirtysix-to-sixty-months',
  'urn:pg:data-point:insured-risk-activity-adverse-driving',
];

const AdverseDrivingDetails: React.FC = () => {
  const activeUserUuid = useSelector((app) => app.activeUserUuid);
  const navigate = useNavigate();
  const [{ enabled: funnelRemixP2 }] = useDecision(Flags.LIFE_FUNNEL_REMIX_P2);

  const { data, loading } = useGetDataPointValues({ params: { urns: ADVERSE_DRIVING_URNS } });
  const { createDataPointValuesFromInterview } = useCreateDataPointValues({
    onCompleted: () => {
      completedNavigatorStep(StepName.ADVERSE_DRIVING_DETAILS, activeUserUuid);

      funnelRemixP2
        ? navigate(`../${CONTINUE_ONLINE_FINANCES_PATH}`)
        : navigate(`../${CONTINUE_ONLINE_MILITARY_SERVICE_PATH}`);
    },
  });
  const onSubmit = async (values: FormValuesOutput<FormValues>) => {
    await createDataPointValuesFromInterview(values, schema);
  };

  if (loading) {
    return <GlobalLoader />;
  }

  return (
    <NavigatorContent>
      <DynamicFormWrapper>
        <DynamicForm<FormValues>
          name="adverse-driving-details"
          schema={schema}
          onSubmit={(values) => onSubmit(values)}
          initialValues={data?.dataPointValues}
        />
      </DynamicFormWrapper>
    </NavigatorContent>
  );
};

export default AdverseDrivingDetails;
